import { RouteData } from '@cya-routes/shared'
import { Mountain, Route, Car, Bike } from 'lucide-react'
import { Tooltip } from '../ui/Tooltip'

interface SurfaceBreakdownProps {
  route: RouteData
}

export function SurfaceBreakdown({ route }: SurfaceBreakdownProps) {
  const { surfaceBreakdown, highwayBreakdown } = route.stats

  if (!surfaceBreakdown || !highwayBreakdown) {
    return null
  }

  // Process highway types: group small ones under "Other"
  const processedHighways = (() => {
    const allHighways = Object.entries(highwayBreakdown)
      .filter(([, stats]) => stats.distance > 0)
      .sort(([, a], [, b]) => b.distance - a.distance)
    
    const majorHighways = allHighways.filter(([, stats]) => stats.percentage >= 3)
    const minorHighways = allHighways.filter(([, stats]) => stats.percentage < 3)
    
    const result = [...majorHighways]
    
    // Combine minor highways into "Other" if there are any
    if (minorHighways.length > 0) {
      const otherDistance = minorHighways.reduce((sum, [, stats]) => sum + stats.distance, 0)
      const otherPercentage = minorHighways.reduce((sum, [, stats]) => sum + stats.percentage, 0)
      
      result.push(['Other', { distance: otherDistance, percentage: otherPercentage }])
    }
    
    return result
  })()
  
  const sortedHighways = processedHighways

  const formatDistance = (km: number) => {
    return km < 1 ? `${Math.round(km * 1000)}m` : `${Math.round(km)}km`
  }

  const getHighwayIcon = (highway: string) => {
    const normalized = highway.toLowerCase()
    if (normalized === 'other') {
      return <Route className="w-3 h-3" />
    }
    if (normalized.includes('bike') || normalized.includes('cycleway')) {
      return <Bike className="w-3 h-3" />
    }
    if (normalized.includes('track') || normalized.includes('trail') || normalized.includes('path')) {
      return <Mountain className="w-3 h-3" />
    }
    if (normalized.includes('motorway') || normalized.includes('trunk') || normalized.includes('primary')) {
      return <Car className="w-3 h-3" />
    }
    return <Route className="w-3 h-3" />
  }

  const getHighwayColor = (highway: string) => {
    const normalized = highway.toLowerCase()
    if (normalized === 'other') return 'text-gray-400'
    if (normalized.includes('bike') || normalized.includes('cycleway')) return 'text-cyan-400'
    if (normalized.includes('motorway')) return 'text-blue-400'
    if (normalized.includes('trunk')) return 'text-purple-400'
    if (normalized.includes('primary')) return 'text-red-400'
    if (normalized.includes('secondary')) return 'text-orange-400'
    if (normalized.includes('tertiary')) return 'text-yellow-400'
    if (normalized.includes('residential')) return 'text-green-400'
    if (normalized.includes('track') || normalized.includes('trail')) return 'text-amber-400'
    return 'text-gray-400'
  }

  const getHighwayDescription = (highway: string) => {
    const normalized = highway.toLowerCase().trim()
    
    const descriptions: Record<string, string> = {
      'motorway': 'High-speed divided highway with controlled access (like interstates/freeways)',
      'trunk road': 'Major highway connecting cities and regions',
      'primary road': 'Main road linking large towns and cities',
      'secondary road': 'Regional road connecting smaller towns',
      'tertiary road': 'Local road serving villages and neighborhoods',
      'unclassified road': 'Minor public road, typically rural or suburban',
      'residential': 'Roads in residential areas with lower speed limits',
      'service road': 'Access roads to buildings, parking areas, or gas stations',
      'track': 'Unpaved roads for agricultural or forestry use',
      'trail': 'Narrow path for hiking, biking, or horse riding',
      'path': 'Footpath or narrow trail, often unpaved',
      'cycleway': 'Dedicated cycling path or bike lane',
      'footway': 'Pedestrian walkway or sidewalk',
      'bridleway': 'Path designated for horses and sometimes cyclists',
      'steps': 'Stairway or stepped path',
      'living street': 'Residential street where pedestrians have priority',
      'pedestrian': 'Pedestrian-only area or plaza',
      'road': 'General road classification when type is unknown',
      'unknown': 'Road type could not be determined from map data',
      'other': 'Combined total of minor road types (each <3% of route)'
    }
    
    // Try exact match first
    if (descriptions[normalized]) {
      return descriptions[normalized]
    }
    
    // Try partial matches for compound names
    for (const [key, description] of Object.entries(descriptions)) {
      if (normalized === key || normalized.includes(key.replace(' ', ''))) {
        return description
      }
    }
    
    // Try word-based matching
    const words = normalized.split(' ')
    for (const word of words) {
      if (descriptions[word]) {
        return descriptions[word]
      }
    }
    
    return `${highway}: Road or path type - classification varies by region`
  }

  return (
    <div className="space-y-4">
      {/* Surface Breakdown */}
      <div>
        <h4 className="text-xs font-medium text-gray-100 mb-2 flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
          Surface Analysis
        </h4>
        
        <div className="space-y-2">
          {/* Paved Surface */}
          <div className="flex items-center justify-between text-xs">
            <div className="flex items-center gap-2">
              <div className="w-3 h-1 bg-gray-500 rounded-full"></div>
              <span className="text-gray-300">Paved</span>
            </div>
            <div className="text-gray-400 font-mono">
              {formatDistance(surfaceBreakdown.paved.distance)} ({Math.round(surfaceBreakdown.paved.percentage)}%)
            </div>
          </div>
          
          {/* Unpaved Surface */}
          <div className="flex items-center justify-between text-xs">
            <div className="flex items-center gap-2">
              <div className="w-3 h-1 bg-amber-500 rounded-full"></div>
              <span className="text-gray-300">Unpaved</span>
            </div>
            <div className="text-gray-400 font-mono">
              {formatDistance(surfaceBreakdown.unpaved.distance)} ({Math.round(surfaceBreakdown.unpaved.percentage)}%)
            </div>
          </div>
          
          {/* Visual Progress Bar */}
          <div className="mt-2">
            <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
              <div className="h-full flex">
                <div 
                  className="bg-gray-500 h-full"
                  style={{ width: `${surfaceBreakdown.paved.percentage}%` }}
                ></div>
                <div 
                  className="bg-amber-500 h-full"
                  style={{ width: `${surfaceBreakdown.unpaved.percentage}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Highway Type Breakdown */}
      <div>
        <h4 className="text-xs font-medium text-gray-100 mb-2 flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-blue-500"></div>
          Highway Types
        </h4>
        
        <div className="space-y-1.5 max-h-32 overflow-y-auto">
          {sortedHighways.map(([highway, stats]) => (
            <div key={highway} className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <div className={`${getHighwayColor(highway)}`}>
                  {getHighwayIcon(highway)}
                </div>
                <Tooltip content={getHighwayDescription(highway)}>
                  <span className="text-gray-300 truncate">
                    {highway}
                  </span>
                </Tooltip>
              </div>
              <div className="text-gray-400 font-mono text-right ml-2 flex-shrink-0">
                {formatDistance(stats.distance)} ({Math.round(stats.percentage)}%)
              </div>
            </div>
          ))}
        </div>
        
        {sortedHighways.length === 0 && (
          <p className="text-xs text-gray-500 italic">No highway data available</p>
        )}
      </div>
    </div>
  )
}